import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";

import { makeAppTheme } from "../theme";

const THEME_STORAGE_KEY = "ledger_theme_mode";
const DEFAULT_THEME_MODE = "light";
const ThemeModeContext = createContext({
  mode: DEFAULT_THEME_MODE,
  isDarkMode: false,
  toggleThemeMode: () => {},
  setThemeMode: () => {},
});

const getInitialThemeMode = () => {
  if (typeof window === "undefined") return DEFAULT_THEME_MODE;

  const storedMode = window.localStorage.getItem(THEME_STORAGE_KEY);
  return storedMode === "dark" || storedMode === "light" ? storedMode : DEFAULT_THEME_MODE;
};

export const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialThemeMode);
  const theme = useMemo(() => makeAppTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);

    const themeColor = mode === "dark" ? "#11130f" : "#f5f3ee";
    document
      .querySelectorAll('meta[name="theme-color"]')
      .forEach((metaTag) => metaTag.setAttribute("content", themeColor));
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      isDarkMode: mode === "dark",
      setThemeMode: (nextMode) => {
        setMode(nextMode === "dark" ? "dark" : "light");
      },
      toggleThemeMode: () => {
        setMode((currentMode) => (currentMode === "dark" ? "light" : "dark"));
      },
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
